import {
  isOrgFavorited,
  isOrgInMyOrgs as isOrgInMyOrgsShared,
  matchesOrgFilterMode as matchesOrgFilterModeShared,
  type OrgMenuFilterMode,
} from '@pypestream/design-system';
import { ShortOrgType } from './permissions-table.types';

export type { OrgMenuFilterMode };
export { isOrgFavorited };

type FilterableOrg = Pick<ShortOrgType, 'id' | 'isPrimaryAccount'>;

/**
 * The shared predicates (design-system `smart-components/utils/org-favorites.ts`,
 * PSTUDIO-3330 Unit 1 — also exported publicly from `@pypestream/design-system`
 * so this app does not keep a second copy of the "is this org in My Orgs" rule)
 * compare a candidate org's id against a single `primaryOrgId` — the shape a
 * TREE naturally has one of. Manager's `ShortOrgType` is a FLAT array where
 * each org already carries its OWN `isPrimaryAccount` boolean, so there is
 * nothing to look up: an org is its own primary-org candidate, or it is not.
 */
function primaryOrgIdFor(org: FilterableOrg): string | undefined {
  return org.isPrimaryAccount ? org.id : undefined;
}

/**
 * Whether `org` counts as being "in My Orgs" — either explicitly favorited,
 * or the primary/top org, which is always in My Orgs and never
 * user-togglable.
 */
export function isOrgInMyOrgs(
  org: FilterableOrg,
  favoriteOrgIds: string[] | undefined
): boolean {
  return isOrgInMyOrgsShared(org, favoriteOrgIds, primaryOrgIdFor(org));
}

/** Whether `org` should be shown under the given filter mode. */
export function matchesOrgFilterMode(
  org: FilterableOrg,
  mode: OrgMenuFilterMode,
  favoriteOrgIds: string[] | undefined
): boolean {
  return matchesOrgFilterModeShared(org, {
    mode,
    favoriteOrgIds,
    primaryOrgId: primaryOrgIdFor(org),
  });
}

/** The orgs matching the given filter mode, client-side (flat, no tree). */
export function filterOrgsBySegment(
  orgs: ShortOrgType[],
  mode: OrgMenuFilterMode,
  favoriteOrgIds: string[] | undefined
): ShortOrgType[] {
  return orgs.filter((org) => matchesOrgFilterMode(org, mode, favoriteOrgIds));
}

/** Number of orgs matching the given filter mode — feeds the segment counts. */
export function countOrgsMatchingMode(
  orgs: ShortOrgType[],
  mode: OrgMenuFilterMode,
  favoriteOrgIds: string[] | undefined
): number {
  return orgs.reduce(
    (total, org) =>
      total + (matchesOrgFilterMode(org, mode, favoriteOrgIds) ? 1 : 0),
    0
  );
}

export type OrgStarState = {
  icon: 'star' | 'star-filled';
  readOnly: boolean;
};

/**
 * Pure: which star icon an org row shows and whether it is clickable.
 * The primary/top org is always favorited and never user-togglable — it
 * reads filled and read-only regardless of `canEditFavorites`. Every other
 * org reflects `favoriteOrgIds` and is clickable only when the viewer may
 * edit favorites (their own profile — see `user-details.tsx`).
 *
 * Deliberately kept in this design-system/React-free module rather than in
 * `org-cell.tsx` itself: that file pulls several design-system Lit-backed
 * React wrappers (`TableCell`, `Stack`, `Icon`, …), and importing it from a
 * vitest file fails at module load ("Denied ID …global-styles.scss?inline")
 * — confirmed by actually trying it, not assumed. Keeping this predicate
 * here is what makes it unit-testable at all.
 */
export function resolveOrgStarState(
  org: FilterableOrg,
  favoriteOrgIds: string[],
  canEditFavorites: boolean
): OrgStarState {
  if (org.isPrimaryAccount) {
    return { icon: 'star-filled', readOnly: true };
  }

  return {
    icon: isOrgFavorited(org.id, favoriteOrgIds) ? 'star-filled' : 'star',
    readOnly: !canEditFavorites,
  };
}
